import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import {
  HR_ROSTER_ASSIGNMENT_STATUSES,
  HR_ROSTER_ASSIGNMENT_TYPES,
  HR_ROSTER_PLAN_STATUSES,
  HR_WEEKDAYS,
  HrCoverageRequirement,
  HrEmployee,
  HrEmployeeWorkPattern,
  HrRosterAssignment,
  HrRosterChangeLog,
  HrRosterConflict,
  HrRosterDashboard,
  HrRosterPlan,
  HrShiftTemplate
} from '../hr.models';
import { HrService } from '../hr.service';
import { HrActionDialogComponent } from '../shared/hr-action-dialog.component';
import { HrListTabsComponent } from '../shared/hr-list-tabs.component';
import { HrModuleNavComponent } from '../shared/hr-module-nav.component';

@Component({
  selector: 'app-hr-roster',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent,
    PageFormSectionComponent,
    HrActionDialogComponent,
    HrListTabsComponent,
    HrModuleNavComponent
  ],
  providers: [MessageService],
  templateUrl: './hr-roster.component.html',
  styleUrls: ['./hr-roster.component.scss']
})
export class HrRosterComponent implements OnInit {
  activeListTab = 'plans';
  private readonly hrService = inject(HrService);
  private readonly permissionService = inject(PermissionService);
  private readonly messageService = inject(MessageService);

  loading = false;
  dashboard: HrRosterDashboard = {};
  plans: HrRosterPlan[] = [];
  templates: HrShiftTemplate[] = [];
  workPatterns: HrEmployeeWorkPattern[] = [];
  assignments: HrRosterAssignment[] = [];
  coverage: HrCoverageRequirement[] = [];
  conflicts: HrRosterConflict[] = [];
  changeLog: HrRosterChangeLog[] = [];
  employees: HrEmployee[] = [];
  selectedPlan?: HrRosterPlan;
  resourceId?: number;

  planForm: HrRosterPlan = this.emptyPlan();
  assignmentForm: HrRosterAssignment = this.emptyAssignment();
  templateForm: HrShiftTemplate = this.emptyTemplate();
  workPatternForm: HrEmployeeWorkPattern = this.emptyWorkPattern();
  coverageForm: HrCoverageRequirement = this.emptyCoverage();
  activeDialog?: 'plan' | 'assignment' | 'template' | 'pattern' | 'coverage';

  readonly canManageRoster = this.permissionService.hasPermission(app_permissions.hr.roster.manage);
  readonly canPublishRoster = this.permissionService.hasPermission(app_permissions.hr.roster.publish);
  readonly planStatusOptions = HR_ROSTER_PLAN_STATUSES.map(value => ({ label: this.format(value), value }));
  readonly assignmentStatusOptions = HR_ROSTER_ASSIGNMENT_STATUSES.map(value => ({ label: this.format(value), value }));
  readonly assignmentTypeOptions = HR_ROSTER_ASSIGNMENT_TYPES.map(value => ({ label: this.format(value), value }));
  workerCategoryOptions: { label: string; value: string }[] = [];
  readonly weekdayOptions = HR_WEEKDAYS;

  ngOnInit(): void {
    this.loadWorkerCategories();
    this.loadEmployees();
    this.load();
  }

  private loadWorkerCategories(): void {
    this.hrService.workerCategories().subscribe(response => {
      this.workerCategoryOptions = (response.data ?? []).map(item => ({
        label: item.categoryName || this.format(item.categoryCode), value: item.categoryCode || ''
      }));
    });
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Draft Plans', value: this.dashboard.draftPlans || 0, tone: 'info' },
      { label: 'Published', value: this.dashboard.publishedPlans || 0, tone: 'success' },
      { label: 'Open Conflicts', value: this.dashboard.openConflicts || 0, tone: (this.dashboard.openConflicts || 0) > 0 ? 'danger' : 'success' },
      { label: 'This Week', value: this.dashboard.assignmentsThisWeek || 0 }
    ];
  }

  get hasBlockers(): boolean {
    return this.conflicts.some(conflict => conflict.severity === 'BLOCKER' && conflict.conflictStatus === 'OPEN');
  }

  get templateOptions(): { label: string; value: number | undefined }[] {
    return [
      { label: 'No template', value: undefined },
      ...this.templates.map(template => ({
        label: `${template.templateName || template.templateCode || 'Template'} (${template.startTime || '-'}-${template.endTime || '-'})`,
        value: template.shiftTemplateId
      }))
    ];
  }

  get employeeOptions(): { label: string; value: number | undefined }[] {
    return this.employees.map(employee => ({
      label: this.employeeLabel(employee),
      value: employee.employeeId
    }));
  }

  get calendarDays(): { date: string; label: string; rows: HrRosterAssignment[] }[] {
    if (!this.selectedPlan?.periodStart || !this.selectedPlan?.periodEnd) {
      return [];
    }
    const days: { date: string; label: string; rows: HrRosterAssignment[] }[] = [];
    let cursor = new Date(`${this.selectedPlan.periodStart}T00:00:00`);
    const end = new Date(`${this.selectedPlan.periodEnd}T00:00:00`);
    while (cursor <= end) {
      const date = this.dateInput(cursor);
      days.push({
        date,
        label: cursor.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }),
        rows: this.assignments.filter(assignment => assignment.workDate === date)
      });
      cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1);
    }
    return days;
  }

  load(): void {
    this.loading = true;
    this.hrService.rosterDashboard().subscribe({
      next: response => {
        this.dashboard = response.data || {};
        this.plans = this.dashboard.plans || [];
        if (this.selectedPlan?.rosterPlanId) {
          const refreshed = this.plans.find(plan => plan.rosterPlanId === this.selectedPlan?.rosterPlanId);
          if (refreshed) {
            this.selectedPlan = refreshed;
          }
        }
        this.loadTemplates();
        this.loadWorkPatterns();
        if (this.selectedPlan?.rosterPlanId) {
          this.loadPlanDetails(this.selectedPlan.rosterPlanId);
        }
      },
      error: () => this.toast('error', 'Roster dashboard could not be loaded.'),
      complete: () => this.loading = false
    });
  }

  selectPlan(plan: HrRosterPlan): void {
    this.selectedPlan = plan;
    this.planForm = { ...plan };
    if (plan.rosterPlanId) {
      this.loadPlanDetails(plan.rosterPlanId);
    }
  }

  editPlan(plan: HrRosterPlan): void {
    this.selectPlan(plan);
    this.activeDialog = 'plan';
  }

  savePlan(): void {
    this.hrService.saveRosterPlan(this.planForm).subscribe({
      next: response => {
        this.activeDialog = undefined;
        this.selectedPlan = response.data;
        this.toast('success', 'Roster plan saved.');
        this.load();
      },
      error: () => this.toast('error', 'Roster plan could not be saved.')
    });
  }

  resetPlan(): void {
    this.planForm = this.emptyPlan();
  }

  saveAssignment(): void {
    if (!this.selectedPlan?.rosterPlanId) {
      return;
    }
    const request = this.applyResource({ ...this.assignmentForm, rosterPlanId: this.selectedPlan.rosterPlanId });
    this.hrService.saveRosterAssignment(this.selectedPlan.rosterPlanId, request).subscribe({
      next: () => {
        this.activeDialog = undefined;
        this.toast('success', 'Roster assignment saved.');
        this.resetAssignment();
        this.loadPlanDetails(this.selectedPlan!.rosterPlanId!);
        this.load();
      },
      error: () => this.toast('error', 'Roster assignment could not be saved.')
    });
  }

  editAssignment(assignment: HrRosterAssignment): void {
    this.assignmentForm = {
      ...assignment,
      startTime: this.toDateTimeLocal(assignment.startTime),
      endTime: this.toDateTimeLocal(assignment.endTime)
    };
    this.resourceId = assignment.deliveryId || assignment.shipmentId || assignment.mobilizationId
      || assignment.warehouseId || assignment.clearanceAssignmentId || assignment.projectId;
    this.activeDialog = 'assignment';
  }

  deleteAssignment(assignment: HrRosterAssignment): void {
    if (!assignment.rosterAssignmentId || !this.selectedPlan?.rosterPlanId) {
      return;
    }
    this.hrService.deleteRosterAssignment(assignment.rosterAssignmentId).subscribe({
      next: () => {
        this.toast('success', 'Roster assignment deleted.');
        this.loadPlanDetails(this.selectedPlan!.rosterPlanId!);
        this.load();
      },
      error: () => this.toast('error', 'Roster assignment could not be deleted.')
    });
  }

  resetAssignment(): void {
    this.assignmentForm = this.emptyAssignment();
    this.resourceId = undefined;
  }

  saveTemplate(): void {
    this.hrService.saveShiftTemplate(this.templateForm).subscribe({
      next: () => {
        this.activeDialog = undefined;
        this.toast('success', 'Shift template saved.');
        this.resetTemplate();
        this.loadTemplates();
      },
      error: () => this.toast('error', 'Shift template could not be saved.')
    });
  }

  resetTemplate(): void {
    this.templateForm = this.emptyTemplate();
  }

  saveWorkPattern(): void {
    this.hrService.saveEmployeeWorkPattern(this.workPatternForm).subscribe({
      next: () => {
        this.activeDialog = undefined;
        this.toast('success', 'Weekly work pattern saved.');
        this.resetWorkPattern();
        this.loadWorkPatterns();
      },
      error: () => this.toast('error', 'Weekly work pattern could not be saved.')
    });
  }

  editWorkPattern(pattern: HrEmployeeWorkPattern): void {
    this.workPatternForm = { ...pattern };
    this.activeDialog = 'pattern';
  }

  resetWorkPattern(): void {
    this.workPatternForm = this.emptyWorkPattern();
  }

  saveCoverage(): void {
    if (!this.selectedPlan?.rosterPlanId) {
      return;
    }
    this.hrService.saveCoverageRequirement({ ...this.coverageForm, rosterPlanId: this.selectedPlan.rosterPlanId }).subscribe({
      next: () => {
        this.activeDialog = undefined;
        this.toast('success', 'Coverage requirement saved.');
        this.resetCoverage();
        this.loadPlanDetails(this.selectedPlan!.rosterPlanId!);
      },
      error: () => this.toast('error', 'Coverage requirement could not be saved.')
    });
  }

  resetCoverage(): void {
    this.coverageForm = this.emptyCoverage();
  }

  openActionDialog(dialog: 'plan' | 'assignment' | 'template' | 'pattern' | 'coverage'): void {
    if (dialog === 'plan') this.resetPlan();
    if (dialog === 'assignment') this.resetAssignment();
    if (dialog === 'template') this.resetTemplate();
    if (dialog === 'pattern') this.resetWorkPattern();
    if (dialog === 'coverage') this.resetCoverage();
    this.activeDialog = dialog;
  }

  actionDialogVisible(dialog: 'plan' | 'assignment' | 'template' | 'pattern' | 'coverage'): boolean {
    return this.activeDialog === dialog;
  }

  setActionDialogVisible(dialog: 'plan' | 'assignment' | 'template' | 'pattern' | 'coverage', visible: boolean): void {
    this.activeDialog = visible ? dialog : undefined;
  }

  recalculateConflicts(): void {
    if (!this.selectedPlan?.rosterPlanId) {
      return;
    }
    this.hrService.recalculateRosterConflicts(this.selectedPlan.rosterPlanId).subscribe({
      next: response => {
        this.conflicts = response.data || [];
        this.toast('success', 'Roster conflicts recalculated.');
        this.load();
      },
      error: () => this.toast('error', 'Roster conflicts could not be recalculated.')
    });
  }

  generateFromPatterns(): void {
    if (!this.selectedPlan?.rosterPlanId) {
      return;
    }
    this.hrService.generateRosterFromWorkPatterns(this.selectedPlan.rosterPlanId).subscribe({
      next: response => {
        const count = response.data?.length || 0;
        this.toast('success', `${count} roster assignments generated.`);
        this.loadPlanDetails(this.selectedPlan!.rosterPlanId!);
        this.load();
      },
      error: () => this.toast('error', 'Roster assignments could not be generated.')
    });
  }

  publishPlan(): void {
    if (!this.selectedPlan?.rosterPlanId) {
      return;
    }
    this.hrService.publishRosterPlan(this.selectedPlan.rosterPlanId).subscribe({
      next: response => {
        this.selectedPlan = response.data;
        this.toast('success', 'Roster plan published.');
        this.load();
      },
      error: () => this.toast('error', 'Roster plan could not be published.')
    });
  }

  unpublishPlan(): void {
    if (!this.selectedPlan?.rosterPlanId) {
      return;
    }
    this.hrService.unpublishRosterPlan(this.selectedPlan.rosterPlanId).subscribe({
      next: response => {
        this.selectedPlan = response.data;
        this.toast('success', 'Roster plan unpublished.');
        this.load();
      },
      error: () => this.toast('error', 'Roster plan could not be unpublished.')
    });
  }

  format(value?: string): string {
    return (value || '-').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
  }

  planSeverity(status?: string): 'success' | 'secondary' | 'warn' | 'danger' | 'info' | 'contrast' {
    if (status === 'PUBLISHED') {
      return 'success';
    }
    if (status === 'UNPUBLISHED') {
      return 'warn';
    }
    return 'secondary';
  }

  resourceLabel(assignment: HrRosterAssignment): string {
    const pairs = [
      ['Delivery', assignment.deliveryId],
      ['Shipment', assignment.shipmentId],
      ['Mobilization', assignment.mobilizationId],
      ['Warehouse', assignment.warehouseId],
      ['Clearance', assignment.clearanceAssignmentId],
      ['Project', assignment.projectId]
    ];
    const match = pairs.find(([, value]) => Boolean(value));
    return match ? `${match[0]} #${match[1]}` : '-';
  }

  weekdayLabel(value?: number): string {
    return this.weekdayOptions.find(option => option.value === value)?.label || '-';
  }

  private loadEmployees(): void {
    this.hrService.employees({ owningCompanyId: 250, pageSize: 500 }).subscribe({
      next: response => this.employees = response.items || [],
      error: () => this.toast('error', 'Employee dropdown could not be loaded.')
    });
  }

  private loadTemplates(): void {
    this.hrService.shiftTemplates().subscribe({
      next: response => this.templates = response.data || [],
      error: () => this.toast('error', 'Shift templates could not be loaded.')
    });
  }

  private loadWorkPatterns(): void {
    this.hrService.employeeWorkPatterns(undefined, undefined, true).subscribe({
      next: response => this.workPatterns = response.data || [],
      error: () => this.toast('error', 'Weekly work patterns could not be loaded.')
    });
  }

  private loadPlanDetails(rosterPlanId: number): void {
    this.hrService.rosterAssignments(rosterPlanId).subscribe({
      next: response => this.assignments = response.data || [],
      error: () => this.toast('error', 'Roster assignments could not be loaded.')
    });
    this.hrService.rosterConflicts(rosterPlanId).subscribe({
      next: response => this.conflicts = response.data || [],
      error: () => this.toast('error', 'Roster conflicts could not be loaded.')
    });
    this.hrService.coverageRequirements(undefined, rosterPlanId).subscribe({
      next: response => this.coverage = response.data || [],
      error: () => this.toast('error', 'Coverage requirements could not be loaded.')
    });
    this.hrService.rosterChangeLog(rosterPlanId, undefined, 100).subscribe({
      next: response => this.changeLog = response.data || [],
      error: () => this.toast('error', 'Roster change log could not be loaded.')
    });
  }

  private emptyPlan(): HrRosterPlan {
    const today = new Date();
    const start = this.dateInput(today);
    const endDate = new Date(today);
    endDate.setDate(today.getDate() + 6);
    return {
      planStatus: 'DRAFT',
      periodStart: start,
      periodEnd: this.dateInput(endDate)
    };
  }

  private emptyAssignment(): HrRosterAssignment {
    return {
      assignmentType: 'GENERAL',
      assignmentStatus: 'DRAFT',
      workDate: this.dateInput(new Date()),
      breakMinutes: 0
    };
  }

  private emptyTemplate(): HrShiftTemplate {
    return {
      assignmentType: 'GENERAL',
      workerCategory: 'OFFICE',
      startTime: '08:00',
      endTime: '17:00',
      breakMinutes: 60,
      active: true
    };
  }

  private emptyWorkPattern(): HrEmployeeWorkPattern {
    return {
      dayOfWeek: 1,
      assignmentType: 'OFFICE_SHIFT',
      startTime: '09:00',
      endTime: '17:00',
      breakMinutes: 0,
      effectiveFrom: this.dateInput(new Date()),
      active: true
    };
  }

  private emptyCoverage(): HrCoverageRequirement {
    return {
      requirementDate: this.dateInput(new Date()),
      assignmentType: 'GENERAL',
      requiredCount: 1,
      active: true
    };
  }

  private employeeLabel(employee: HrEmployee): string {
    const name = employee.displayName || `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || 'Employee';
    const number = employee.employeeNumber || employee.employeeId || '-';
    const department = employee.departmentName ? ` - ${employee.departmentName}` : '';
    return `${name} (${number})${department}`;
  }

  private applyResource(request: HrRosterAssignment): HrRosterAssignment {
    request.deliveryId = undefined;
    request.shipmentId = undefined;
    request.mobilizationId = undefined;
    request.warehouseId = undefined;
    request.clearanceAssignmentId = undefined;
    request.projectId = undefined;
    if (!this.resourceId) {
      return request;
    }
    if (request.assignmentType === 'SHIPMENT_DELIVERY') {
      request.deliveryId = this.resourceId;
    } else if (request.assignmentType === 'FLEET_MOBILIZATION') {
      request.mobilizationId = this.resourceId;
    } else if (request.assignmentType === 'WAREHOUSE_SHIFT') {
      request.warehouseId = this.resourceId;
    } else if (request.assignmentType === 'CLEARANCE_ASSIGNMENT') {
      request.clearanceAssignmentId = this.resourceId;
    } else if (request.assignmentType === 'POSM_PROJECT') {
      request.projectId = this.resourceId;
    }
    return request;
  }

  private dateInput(value: Date): string {
    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private toDateTimeLocal(value?: string): string | undefined {
    if (!value) {
      return undefined;
    }
    return value.slice(0, 16);
  }

  private toast(severity: 'success' | 'error', detail: string): void {
    this.messageService.add({ severity, summary: severity === 'success' ? 'Saved' : 'Unable to complete', detail });
  }
}
